/**
 * Semantic tokens — the ONLY color/elevation surface features (via primitives) should
 * ever reference. Each is a `{ base, _dark }` pair (base = light). Every text/surface
 * and text/action pairing is WCAG AA verified (see docs in the redesign report);
 * ratios are noted inline.
 *
 * Chakra v3 semantic-token shape: `{ value: { base, _dark } }`, referencing raw tokens
 * as `{colors.blue.500}` where useful, or literal hex where a value is verified exact.
 */

const c = (base: string, _dark: string) => ({ value: { base, _dark } });

export const semanticColors = {
  /* ---- surfaces ---------------------------------------------------------- */
  surface: {
    // default content surface: white (light) / near-black raised base (dark)
    default: c('{colors.ink.0}', '{colors.ink.900}'), // ink 18.42 / 17.67
    // elevated (menus, popovers, cards) — differentiated by shadow + border
    raised: c('{colors.ink.0}', '{colors.ink.800}'), // ink 18.42 / 16.37
    // recessed canvas / subtle fills / code — EHE pale-blue in light
    subtle: c('{colors.ink.150}', '{colors.ink.750}'), // ink 15.80 / 14.84
    // inverse surface (tooltips, contrast banners)
    inverse: c('{colors.ink.950}', '{colors.ink.50}'),
  },

  /* ---- text -------------------------------------------------------------- */
  text: {
    primary: c('{colors.ink.950}', '{colors.ink.50}'), // 18.42 / 17.67 on default
    secondary: c('{colors.ink.600}', '{colors.ink.300}'), // 9.37 / 11.39
    muted: c('{colors.ink.500}', '{colors.ink.400}'), // 5.91 / 6.94
    inverse: c('{colors.ink.0}', '{colors.ink.950}'), // on action.primary: 5.34 / 5.11
    link: c('{colors.blue.500}', '#4c8cff'), // 5.34 / 5.34 (subtle 4.58 / 5.01)
  },

  /* ---- borders ----------------------------------------------------------- */
  border: {
    subtle: c('{colors.ink.100}', 'rgba(255,255,255,0.07)'),
    default: c('{colors.ink.200}', 'rgba(255,255,255,0.12)'),
    strong: c('{colors.ink.300}', 'rgba(255,255,255,0.22)'),
  },

  /* ---- actions ----------------------------------------------------------- */
  action: {
    primary: c('{colors.blue.500}', '{colors.blue.400}'), // text.inverse: 5.34 / 5.11
    primaryHover: c('{colors.blue.600}', '#5b9bff'), // text.inverse: 6.84 / 6.65
    primaryActive: c('{colors.blue.700}', '#7db0ff'),
    // subtle/tonal secondary fill (paired with text.primary)
    secondary: c('{colors.ink.100}', '{colors.ink.750}'),
    secondaryHover: c('{colors.ink.200}', '{colors.ink.700}'),
  },

  /* ---- brand (pink, used with intent) ------------------------------------ */
  brand: {
    solid: c('{colors.brand.500}', '#f6b3e1'), // brand.onSolid: 9.04 / 10.93
    solidHover: c('{colors.brand.600}', '#f7c6e8'),
    onSolid: c('{colors.ink.950}', '{colors.ink.950}'), // dark ink, NEVER white
    subtle: c('{colors.brand.100}', '#3a1c30'),
    fg: c('{colors.brand.800}', '#f3b0e0'), // on subtle: 6.29 / 8.71
  },

  /* ---- status (kept distinct from brand) --------------------------------- */
  status: {
    success: c('{colors.green.light}', '{colors.green.dark}'),
    successSubtle: c('{colors.green.subtleBg}', '{colors.green.subtleBgDark}'),
    successFg: c('{colors.green.onSubtle}', '{colors.green.onSubtleDark}'),
    warning: c('{colors.amber.light}', '{colors.amber.dark}'),
    warningSubtle: c('{colors.amber.subtleBg}', '{colors.amber.subtleBgDark}'),
    warningFg: c('{colors.amber.onSubtle}', '{colors.amber.onSubtleDark}'),
    danger: c('{colors.red.light}', '{colors.red.dark}'),
    dangerSubtle: c('{colors.red.subtleBg}', '{colors.red.subtleBgDark}'),
    dangerFg: c('{colors.red.onSubtle}', '{colors.red.onSubtleDark}'),
    info: c('{colors.cyan.light}', '{colors.cyan.dark}'),
    infoSubtle: c('{colors.cyan.subtleBg}', '{colors.cyan.subtleBgDark}'),
    infoFg: c('{colors.cyan.onSubtle}', '{colors.cyan.onSubtleDark}'),
  },

  /* ---- overlay / scrim --------------------------------------------------- */
  overlay: {
    // Modal backdrop scrim behind dialogs + the command menu. A near-black wash in both
    // modes (deeper in dark) so the app dims consistently — the single source for scrims.
    backdrop: c('rgba(20,20,20,0.55)', 'rgba(0,0,0,0.65)'),
  },

  /* ---- focus ring -------------------------------------------------------- */
  focusRing: c('{colors.blue.500}', '{colors.blue.400}'), // >=3:1 on all surfaces
} as const;

/** Elevation — border-first philosophy; shadows are restrained, deeper in dark. */
export const semanticShadows = {
  xs: {
    value: {
      base: '0 1px 2px rgba(20,20,20,0.06)',
      _dark: '0 1px 2px rgba(0,0,0,0.5)',
    },
  },
  sm: {
    value: {
      base: '0 1px 2px rgba(20,20,20,0.06), 0 2px 6px rgba(20,20,20,0.06)',
      _dark: '0 1px 2px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.45)',
    },
  },
  md: {
    value: {
      base: '0 2px 4px rgba(20,20,20,0.06), 0 6px 16px rgba(20,20,20,0.10)',
      _dark: '0 2px 4px rgba(0,0,0,0.55), 0 8px 20px rgba(0,0,0,0.5)',
    },
  },
  lg: {
    value: {
      base: '0 8px 24px rgba(20,20,20,0.12), 0 2px 6px rgba(20,20,20,0.08)',
      _dark: '0 12px 32px rgba(0,0,0,0.6), 0 2px 6px rgba(0,0,0,0.5)',
    },
  },
  focus: {
    value: {
      base: '0 0 0 3px rgba(0,95,243,0.35)',
      _dark: '0 0 0 3px rgba(59,130,255,0.45)',
    },
  },
} as const;
